/* eslint-disable sort-keys */
import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Checkbox from '@mui/material/Checkbox';
import AutoComplete from '../Atoms/AutoComplete/AutoComplete';
import TextField from '../Atoms/TextField/TextField';
import Button from '../Atoms/Button/Button';
import {
    ReportGenoratorMain,
    ReportGenoratorContainer,
    ReportGenoratorHeader,
    ReportGenoratorImg,
} from './ReportGenerator.style';
import { PrimaryWrap } from '../Generals/stylesheets/General.style';
import {
    useGenerateReportsMutation,
    useGetStudentSkillLevelReportMutation,
    useGetStudentSkillLevelReportsBatchMutation,
    useGetSectionSkillLevelReportMutation,
    useGetTeacherSkillLevelReportMutation,
    useGetTeacherSkillLevelReportsBatchMutation,
    useGetCourseSkillLevelReportMutation,
    useGetCourseSkillLevelReportsBatchMutation,
    useGetDistrictSkillLevelReportMutation,
    useGetDistrictPMReportMutation,
    useGetTeacherPMReportMutation,
    useReportsFeatureAccessQuery
} from './ReportQueries';
import PastReportsList from './PastReportsList';
import { ReportConfig, ReportOption, AvailableReportsData, ReportLevelConfig } from './types';
import { createPhaseReportConfigs } from './selectors/PhaseReportSelectors';
import { createStudentSkillLevelReportConfigs, createSectionSkillLevelReportConfigs, createTeacherSkillLevelReportConfigs, createCourseSkillLevelReportConfigs, createDistrictSkillLevelReportConfigs } from './selectors/SkillLevelReportSelectors';
import { createDistrictPMReportConfigs, createTeacherPMReportConfigs } from './selectors/PMReportSelectors';
import { toast } from 'react-toastify';

interface ReportGeneratorProps {
    availableReportsData?: AvailableReportsData;
}

const ReportGenerator: React.FC<ReportGeneratorProps> = ({ availableReportsData }) => {
    const history = useHistory();
    const [selectedReportConfig, setSelectedReportConfig] =
        useState<ReportConfig | null>(null);
    const [selections, setSelections] = useState<(ReportOption | ReportOption[])[]>([]);

    const generateReportsMutation = useGenerateReportsMutation();
    const getStudentSkillLevelReportMutation = useGetStudentSkillLevelReportMutation();
    const getStudentSkillLevelReportsBatchMutation =
        useGetStudentSkillLevelReportsBatchMutation();
    const getSectionSkillLevelReportMutation = useGetSectionSkillLevelReportMutation();
    const getTeacherSkillLevelReportMutation = useGetTeacherSkillLevelReportMutation();
    const getDistrictSkillLevelReportMutation = useGetDistrictSkillLevelReportMutation();
    const getCourseSkillLevelReportMutation = useGetCourseSkillLevelReportMutation();
    const getCourseSkillLevelReportsBatchMutation = useGetCourseSkillLevelReportsBatchMutation();
    const getTeacherSkillLevelReportsBatchMutation = useGetTeacherSkillLevelReportsBatchMutation();
    const isAggregatedSkillLevelReportEnabled = useReportsFeatureAccessQuery(
        'aggregated_skill_level_reports',
    );
    const getDistrictPMReportMutation = useGetDistrictPMReportMutation();
    const getTeacherPMReportMutation = useGetTeacherPMReportMutation();
    const isPMReportsEnabled = useReportsFeatureAccessQuery('pm_reports');

    // Transform API response into unified report configs using selectors
    const reportConfigs = useMemo(() => {
        if (!availableReportsData) {
            return [];
        }

        const configs: ReportConfig[] = [];

        // Add phase reports
        const phaseConfigs = createPhaseReportConfigs(availableReportsData, {
            generateReportsMutation,
            history,
        });
        configs.push(...phaseConfigs);

        // Add student skill level reports
        const studentSkillConfigs = createStudentSkillLevelReportConfigs(
            availableReportsData,
            {
                getStudentSkillLevelReportMutation,
                getStudentSkillLevelReportsBatchMutation,
            },
        );
        configs.push(...studentSkillConfigs);

        if (isAggregatedSkillLevelReportEnabled) {
            const sectionSkillConfigs =
                createSectionSkillLevelReportConfigs(availableReportsData, {
                    getSectionSkillLevelReportMutation,
                });
            configs.push(...sectionSkillConfigs);
            const teacherSkillConfigs =
                createTeacherSkillLevelReportConfigs(availableReportsData, {
                    getTeacherSkillLevelReportMutation,
                    getTeacherSkillLevelReportsBatchMutation,
                });
            configs.push(...teacherSkillConfigs);
            const districtSkillConfigs =
                createDistrictSkillLevelReportConfigs(availableReportsData, {
                    getDistrictSkillLevelReportMutation,
                });
            configs.push(...districtSkillConfigs);
            const courseSkillConfigs =
                createCourseSkillLevelReportConfigs(availableReportsData, {
                    getCourseSkillLevelReportMutation,
                    getCourseSkillLevelReportsBatchMutation,
                });
            configs.push(...courseSkillConfigs);
        }
        if (isPMReportsEnabled) {
            const districtPMConfigs = createDistrictPMReportConfigs(availableReportsData, {
                getDistrictPMReportMutation,
            });
            configs.push(...districtPMConfigs);
            const teacherPMConfigs = createTeacherPMReportConfigs(availableReportsData, {
                getTeacherPMReportMutation,
            });
            configs.push(...teacherPMConfigs);
        }
        return configs;
    }, [
        availableReportsData,
        generateReportsMutation,
        history,
        getStudentSkillLevelReportMutation,
        getStudentSkillLevelReportsBatchMutation,
        getSectionSkillLevelReportMutation,
        getTeacherSkillLevelReportMutation,
        getTeacherSkillLevelReportsBatchMutation,
        getDistrictSkillLevelReportMutation,
        getCourseSkillLevelReportMutation,
        getCourseSkillLevelReportsBatchMutation,
        isAggregatedSkillLevelReportEnabled,
        getDistrictPMReportMutation,
        getTeacherPMReportMutation,
        isPMReportsEnabled
    ]);

    const onReportTypeChange = (_e: any, value: ReportConfig | null) => {
        setSelectedReportConfig(value || null);
        setSelections([]); // Clear all selections
    };

    const onSelectionChange = (levelIndex: number, value: ReportOption | ReportOption[] | null) => {
        // Update selection at this level and clear all deeper levels
        const newSelections = selections.slice(0, levelIndex);
        if (value !== null) {
            newSelections[levelIndex] = value;
        }
        setSelections(newSelections);
    };

    // Recursively build the chain of level configs based on current selections
    const levelConfigs = useMemo<ReportLevelConfig[]>(() => {
        if (!selectedReportConfig) return [];

        const configs: ReportLevelConfig[] = [];
        let currentConfig: ReportLevelConfig | null = selectedReportConfig;
        let levelIndex = 0;

        while (currentConfig) {
            configs.push(currentConfig);

            // Get next level config based on current selection
            if (currentConfig.selections && typeof currentConfig.selections === 'function') {
                const currentSelection = selections[levelIndex];
                if (currentSelection) {
                    currentConfig = currentConfig.selections(currentSelection);
                    levelIndex += 1;
                } else {
                    // No selection yet, stop here
                    break;
                }
            } else if (currentConfig.selections === null) {
                // Reached the deepest level
                break;
            } else {
                // Invalid config
                break;
            }
        }

        return configs;
    }, [selectedReportConfig, selections]);

    // Auto-select when a level has exactly one option
    useEffect(() => {
        for (let i = 0; i < levelConfigs.length; i++) {
            const config = levelConfigs[i];
            const existingSelection = selections[i];

            // If not yet selected and exactly 1 option, auto-select it
            if (existingSelection === undefined && config.options?.length === 1) {
                const newSelections = [...selections];
                newSelections[i] = config.multiple ? [config.options[0]] : config.options[0];
                setSelections(newSelections);
                // Only auto-select one level at a time to allow proper cascade
                return;
            }
        }
    }, [levelConfigs, selections]);

    // Find the deepest config with onSubmit
    const deepestConfig = useMemo<{ config: ReportLevelConfig; depth: number } | null>(() => {
        for (let i = levelConfigs.length - 1; i >= 0; i--) {
            if (levelConfigs[i].onSubmit) {
                return { config: levelConfigs[i], depth: i };
            }
        }
        return null;
    }, [levelConfigs]);

    // Check if we can generate (all levels up to deepest have selections)
    const canGenerate = useMemo(() => {
        if (!deepestConfig) return false;

        // Check if all levels up to and including the deepest have valid selections
        for (let i = 0; i <= deepestConfig.depth; i++) {
            const selection = selections[i];
            const config = levelConfigs[i];

            if (!selection) return false;

            // For multiple selection, check if array has items
            if (config.multiple && (!Array.isArray(selection) || selection.length === 0)) {
                return false;
            }
        }

        return true;
    }, [deepestConfig, selections, levelConfigs]);

    const handleGenerateReport = () => {
        if (!canGenerate || !deepestConfig || !selectedReportConfig) return;

        toast.info('Report is being generated.');

        // Call onSubmit with context containing all selections
        deepestConfig.config.onSubmit?.({
            selections: selections.slice(0, deepestConfig.depth + 1),
            config: selectedReportConfig,
        });
    };

    return (
        <PrimaryWrap fullHeight={true} as={undefined as any}>
            <ReportGenoratorMain>
                <ReportGenoratorContainer>
                    <ReportGenoratorHeader data-testid="generator-header">
                        <h1>Report Generator</h1>
                    </ReportGenoratorHeader>
                    <ReportGenoratorImg src="/images/report-generator.png" alt="Report Generator" />
                    <p>
                        Select your query from the dropdown menus below and <br /> hit "GENERATE
                        REPORT(S)"
                    </p>
                    <AutoComplete
                        value={selectedReportConfig}
                        onChange={onReportTypeChange}
                        id="report-type-selector"
                        data-testid="reportTypeSelector"
                        options={reportConfigs}
                        style={{ width: '40%', backgroundColor: '#fff' }}
                        getOptionLabel={(option: ReportConfig) => option.name || ""}
                        autoHighlight
                        renderInput={(params: any) => <TextField {...params} label="Report Type" />}
                        isOptionEqualToValue={(option: ReportConfig, value: ReportConfig) => option.id === value.id}
                    />
                    {levelConfigs.map((levelConfig, levelIndex) => {
                        const levelKey = `${selectedReportConfig?.id ?? 'report'}-${levelConfig.option_name}`;
                        const label = levelConfig.option_name || `Level ${levelIndex + 1}`;
                        const sharedProps = {
                            id: `${levelConfig.option_name.replace(/\s+/g, '-')}-selector`,
                            'data-testid': `${levelKey}-options`,
                            style: { width: '40%', backgroundColor: '#fff' },
                            options: levelConfig.options || [],
                            getOptionLabel: (option: ReportOption) => option.label || '',
                            isOptionEqualToValue: (option: ReportOption, value: ReportOption) =>
                                option.id === value.id,
                            renderInput: (params: any) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label={label}
                                />
                            ),
                        };

                        if (levelConfig.multiple) {
                            return (
                                <AutoComplete<ReportOption, true>
                                    key={levelKey}
                                    {...sharedProps}
                                    multiple
                                    disableCloseOnSelect
                                    value={(selections[levelIndex] as ReportOption[] | undefined) ?? []}
                                    onChange={(_e, value) => onSelectionChange(levelIndex, value)}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                                disableRipple
                                            />
                                            {option.label}
                                        </li>
                                    )}
                                />
                            );
                        }

                        return (
                            <AutoComplete<ReportOption, false>
                                key={levelKey}
                                {...sharedProps}
                                filterSelectedOptions
                                value={(selections[levelIndex] as ReportOption | undefined) ?? null}
                                onChange={(_e, value) => onSelectionChange(levelIndex, value)}
                            />
                        );
                    })}
                    {selectedReportConfig && (
                        <Button
                            variant="contained"
                            style={{ width: '40%' }}
                            onClick={handleGenerateReport}
                            data-testid="generateBtn"
                            disabled={!canGenerate}
                            sx={{ transition: 'none' }}
                        >
                            GENERATE REPORT(S)
                        </Button>
                    )}
                </ReportGenoratorContainer>
                <PastReportsList />
            </ReportGenoratorMain>
        </PrimaryWrap>
    );
};

export default ReportGenerator;

